import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, removePartyMovementBySource } from "@/lib/party-ledger";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { PaymentFormValues } from "@/features/payments/schema";
import type { ClientPaymentListRow, PaymentMethod } from "@/features/payments/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrate(raw: Record<string, unknown>): ClientPaymentListRow {
  const client = raw.clients as { code?: string; name?: string } | null;
  const inv = raw.invoices as { invoice_number?: string } | null;
  return {
    id: String(raw.id),
    client_id: String(raw.client_id),
    invoice_id: (raw.invoice_id as string | null) ?? null,
    payment_date: String(raw.payment_date),
    amount: money(raw.amount),
    payment_method: raw.payment_method as PaymentMethod,
    reference: (raw.reference as string | null) ?? null,
    notes: (raw.notes as string | null) ?? null,
    created_by: (raw.created_by as string | null) ?? null,
    updated_by: (raw.updated_by as string | null) ?? null,
    created_at: String(raw.created_at),
    updated_at: String(raw.updated_at),
    client_code: client?.code ?? null,
    client_name: client?.name ?? null,
    invoice_number: inv?.invoice_number ?? null,
  };
}

async function syncArReceipt(payment: {
  id: string;
  client_id: string;
  amount: number;
  payment_date: string;
  reference: string | null;
  invoice_number?: string | null;
}) {
  await postPartyMovement({
    party: "client",
    partyId: payment.client_id,
    kind: "receipt",
    amount: -Math.abs(payment.amount),
    movementDate: payment.payment_date,
    reference: payment.reference,
    notes: payment.invoice_number ? `Against ${payment.invoice_number}` : null,
    sourceType: "client_payment",
    sourceId: payment.id,
  });
}

export function paymentToForm(row: ClientPaymentListRow): PaymentFormValues {
  return {
    client_id: row.client_id,
    invoice_id: row.invoice_id ?? "",
    payment_date: row.payment_date,
    amount: row.amount,
    payment_method: row.payment_method,
    reference: row.reference ?? "",
    notes: row.notes ?? "",
  };
}

export function useClientPayments(filters?: { clientId?: string; invoiceId?: string }) {
  return useQuery({
    queryKey: [
      "client_payments",
      filters?.clientId ?? "all",
      filters?.invoiceId ?? "all",
    ],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ClientPaymentListRow[]> => {
      let q = supabase
        .from("client_payments")
        .select("*, clients(code, name), invoices(invoice_number)")
        .order("payment_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(300);
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      if (filters?.invoiceId) q = q.eq("invoice_id", filters.invoiceId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => hydrate(row as Record<string, unknown>));
    },
  });
}

/** Issued invoices for a client — for allocation picker */
export function useIssuedInvoicesForClient(clientId?: string) {
  return useQuery({
    queryKey: ["invoices", "issued", clientId ?? "none"],
    enabled: isSupabaseConfigured && Boolean(clientId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id, invoice_number, grand_total, issue_date, status")
        .eq("client_id", clientId!)
        .eq("status", "issued")
        .order("issue_date", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => ({
        id: String(r.id),
        invoice_number: String(r.invoice_number),
        grand_total: money(r.grand_total),
        issue_date: String(r.issue_date),
      }));
    },
  });
}

export function useUpsertClientPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id?: string;
      values: PaymentFormValues;
    }) => {
      const amount = money(values.amount);
      if (amount <= 0) throw new Error("Amount must be greater than 0");

      let invoiceNumber: string | null = null;
      if (text(values.invoice_id)) {
        const { data: inv, error: invErr } = await supabase
          .from("invoices")
          .select("id, client_id, invoice_number, status")
          .eq("id", values.invoice_id)
          .single();
        if (invErr) throw invErr;
        if (inv.status !== "issued") throw new Error("Can only allocate to issued invoices");
        if (String(inv.client_id) !== values.client_id) {
          throw new Error("Invoice belongs to a different client");
        }
        invoiceNumber = String(inv.invoice_number);
      }

      const payload = {
        client_id: values.client_id,
        invoice_id: text(values.invoice_id),
        payment_date: values.payment_date,
        amount,
        payment_method: values.payment_method,
        reference: text(values.reference),
        notes: text(values.notes),
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      let row: ClientPaymentListRow;
      if (id) {
        const { data, error } = await supabase
          .from("client_payments")
          .update(payload)
          .eq("id", id)
          .select("*, clients(code, name), invoices(invoice_number)")
          .single();
        if (error) throw error;
        row = hydrate(data as Record<string, unknown>);
      } else {
        const { data, error } = await supabase
          .from("client_payments")
          .insert({ ...payload, created_by: user?.id ?? null })
          .select("*, clients(code, name), invoices(invoice_number)")
          .single();
        if (error) throw error;
        row = hydrate(data as Record<string, unknown>);
      }

      await syncArReceipt({
        id: row.id,
        client_id: row.client_id,
        amount: row.amount,
        payment_date: row.payment_date,
        reference: row.reference,
        invoice_number: invoiceNumber ?? row.invoice_number,
      });

      return row;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["client_payments"] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.id ? "Payment updated" : "Payment recorded");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save payment"),
  });
}

export function useDeleteClientPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (row: ClientPaymentListRow) => {
      await removePartyMovementBySource({
        party: "client",
        partyId: row.client_id,
        sourceType: "client_payment",
        sourceId: row.id,
      });
      const { error } = await supabase.from("client_payments").delete().eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client_payments"] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Payment deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete payment"),
  });
}
